import dayjs from 'dayjs';
import { MessageBox, Message } from 'element-ui';
const confirmWithBtnLoading = (msg, params, fn, errMsg = '操作失败') => {
    return new Promise((resolve, reject) => {
        MessageBox.confirm(msg, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
            beforeClose: (action, instance, done) => {
                if (action === 'confirm') {
                    instance.confirmButtonLoading = true;
                    fn(params)
                        .then(res => {
                            if (res && +res.code === 0) {
                                Message.success(res.msg || errMsg);
                                done();
                            }
                        })
                        .finally(() => {
                            instance.confirmButtonLoading = false;
                        });
                } else {
                    done();
                }
            }
        })
            .then(() => {
                resolve();
            })
            .catch(e => {
                reject(e);
            });
    });
};

/**
 * @description 二次确认弹窗
 * @param {String} msg 弹窗提示信息
 * @param {Object} param 接口参数
 * @param {Function} behaviour 接口函数，返回值是一个promise对象
 * @param {Function} successCallback 接口请求成功回调函数
 * @param {Function} errorCallback 接口请求失败回调函数
 */
const confirmBtnLoading = (msg, param, behaviour, successCallback, errorCallback = function() {}) => {
    MessageBox.confirm(msg, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                behaviour(param)
                    .then(msg => {
                        Message.success(msg);
                        done();
                    })
                    .catch(errMsg => {
                        Message.error(errMsg);
                    })
                    .finally(() => {
                        instance.confirmButtonLoading = false;
                    });
            } else {
                done();
            }
        }
    })
        .then(res => {
            successCallback();
        })
        .catch(e => {
            errorCallback();
        });
};

/**
 * @description 二次确认弹窗
 * @param {String} msg 弹窗提示信息
 * @param {Object} param 接口参数
 * @param {Function} behaviour 接口函数，返回值是一个promise对象
*/
const confirmBox = (msg, param, behaviour) => {
    MessageBox.confirm(msg, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                behaviour(param).then(() => {
                    done();
                }).finally(() => {
                    instance.confirmButtonLoading = false;
                });
            } else {
                done();
            }
        }
    });
};

/**
 * @description 是否图片类型
 * @param {String} type 文件类型
 */
const isImageType = type => {
    return ['png', 'jpg', 'jpeg', 'gif'].includes(type);
};

/**
 * @description 是否文档类型
 * @param {String} type 文件类型
 */
const isDocType = type => {
    return ['doc', 'docx', 'ppt', 'xlsx'].includes(type);
};

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 * @param {{ span: number, unit: dayjs.ManipulateType, noMessage: boolean, addOneDay: boolean }} 配置时间范围限制
 */
const validateMaxDate = (searchData, sDateField, eDateField, options) => {
    if (searchData[sDateField] && searchData[eDateField]) {
        if (!options) {
            const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
            const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
            const maxDay = 1000 * 60 * 60 * 24 * 63; // 按31天来算，组件问题，得加多一天
            if (eDateTimeStamp - sDateTimeStamp > maxDay) {
                Message.warning(`按时间查询或导出时，查询或导出时间跨度不可超过两个月`);
                return false;
            }
        } else {
            const { span = 2, unit = 'month', noMessage = false, addOneDay = true } = options || {};
            const sDate = dayjs(searchData[sDateField]);
            const eDate = dayjs(searchData[eDateField]);
            let addedTime = sDate.startOf('day')
                .add(span, unit);
            if (addOneDay) {
                addedTime = addedTime.add(1, 'day'); // OTP时间选择组件问题，多加一天
            }
            if (span > 1 && unit === 'month') {
                addedTime = addedTime.add(1, 'day'); // 修复大小月近两月无法选择问题，再多加一天
            }
            const eTime = eDate.endOf('day');
            const isValid = addedTime.isAfter(eTime);
            if (!isValid && !noMessage) {
                const unitNames = {
                    day: '天',
                    month: '个月',
                    week: '周',
                    year: '年'
                };
                Message.warning(`按时间查询或导出时，查询或导出时间跨度不可超过${span}${unitNames[unit]}`);
            }
            return isValid;
        }
    }
    return true;
};

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 * @param {String} maxSearchDay 最大时间跨度
 */
export const validateSearchTime = (searchData, sDateField, eDateField, maxSearchDay = 31, searchType = '时间') => {
    if (searchData[sDateField] && searchData[eDateField]) {
        const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
        const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
        const maxDay = 1000 * 60 * 60 * 24 * (maxSearchDay + 1); // OTP时间组件原因，得加多一天
        if (eDateTimeStamp - sDateTimeStamp > maxDay) {
            Message.warning(`按${searchType}查询或导出时，查询或导出时间跨度不可超过${maxSearchDay}天`);
            return false;
        }
    }
    return true;
};

export { confirmWithBtnLoading, confirmBtnLoading, isImageType, isDocType, validateMaxDate, confirmBox };
